import { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';
import {
  websiteBannerControllerCreateLiveSession,
  websiteBannerControllerLivePreviewUpdate,
} from '@/generated/korza-api-client';
import type {
  CreateWebsiteBannerDto,
  LivePreviewWebsiteBannerDto,
} from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';

export interface UseWebsiteBannerLiveSessionParams {
  bannerId: string | null;
  previewFormSnapshot: CreateWebsiteBannerDto;
  LL: {
    websiteBannerPage: {
      toasts: {
        failedToStartLiveSession: () => string;
        livePreviewUpdated: () => string;
        failedToUpdateLivePreview: () => string;
      };
    };
  };
}

export function useWebsiteBannerLiveSession({
  bannerId,
  previewFormSnapshot,
  LL,
}: UseWebsiteBannerLiveSessionParams) {
  const tenant = useTenant();
  const [liveSessionToken, setLiveSessionToken] = useState<string | null>(null);
  const liveSessionAttemptedRef = useRef(false);

  const liveSessionMutation = useMutation({
    mutationFn: async (opts: {
      path: { bannerId: string };
    }): Promise<string> => {
      const res = await websiteBannerControllerCreateLiveSession(opts);
      const data = res.data;
      const token =
        typeof data === 'object' && data != null && 'token' in data
          ? (data as { token: string }).token
          : typeof data === 'string'
            ? data
            : null;
      if (!token || typeof token !== 'string') {
        throw new Error('No token in live session response');
      }
      return token;
    },
    onSuccess: (token) => setLiveSessionToken(token),
    onError: (err) => {
      liveSessionAttemptedRef.current = false;
      toast.error(
        err instanceof Error
          ? err.message
          : LL.websiteBannerPage.toasts.failedToStartLiveSession()
      );
    },
  });

  useEffect(() => {
    if (!bannerId) {
      liveSessionAttemptedRef.current = false;
      setLiveSessionToken(null);
      return;
    }
    if (
      !liveSessionToken &&
      !liveSessionAttemptedRef.current &&
      !liveSessionMutation.isPending
    ) {
      liveSessionAttemptedRef.current = true;
      liveSessionMutation.mutate({ path: { bannerId } });
    }
  }, [
    bannerId,
    liveSessionToken,
    liveSessionMutation.isPending,
    liveSessionMutation,
  ]);

  const livePreviewUrl =
    liveSessionToken && tenant.storeWebsiteDomain
      ? (() => {
          const base = tenant.storeWebsiteDomain.startsWith('http')
            ? tenant.storeWebsiteDomain
            : `https://${tenant.storeWebsiteDomain}`;
          const u = new URL(base);
          u.searchParams.set('korza-banner-live-preview', liveSessionToken);
          return u.toString();
        })()
      : '';

  const buildLivePreviewDto =
    useCallback((): LivePreviewWebsiteBannerDto | null => {
      if (!bannerId || !liveSessionToken || !tenant.id) return null;
      return {
        tenantId: tenant.id,
        content: previewFormSnapshot.content,
        theme: previewFormSnapshot.theme,
        customStyles: previewFormSnapshot.customStyles,
        position: previewFormSnapshot.position ?? null,
        token: liveSessionToken,
      };
    }, [
      bannerId,
      liveSessionToken,
      tenant.id,
      previewFormSnapshot.content,
      previewFormSnapshot.theme,
      previewFormSnapshot.customStyles,
      previewFormSnapshot.position,
    ]);

  useEffect(() => {
    const dto = buildLivePreviewDto();
    if (!dto) return;
    websiteBannerControllerLivePreviewUpdate({
      path: { bannerId: bannerId! },
      body: dto,
    }).catch(() => {});
  }, [buildLivePreviewDto, bannerId]);

  const forceRefreshMutation = useMutation({
    mutationFn: async () => {
      const dto = buildLivePreviewDto();
      if (!dto) throw new Error('No live session');
      await websiteBannerControllerLivePreviewUpdate({
        path: { bannerId: bannerId! },
        body: dto,
      });
    },
    onSuccess: () =>
      toast.success(LL.websiteBannerPage.toasts.livePreviewUpdated()),
    onError: (err) =>
      toast.error(
        err instanceof Error
          ? err.message
          : LL.websiteBannerPage.toasts.failedToUpdateLivePreview()
      ),
  });

  return {
    liveSessionToken,
    liveSessionMutation,
    livePreviewUrl,
    forceRefreshMutation,
  };
}
